<template>
  <sc-flex :is-column="true">
    <SavingDialog :opened.sync="savingDialogOpened" />

    <WarningDialog
      :opened.sync="pageOutOfRangeDialogOpened"
      warning-title="Page out of range"
      :warning-text="
        `
        The page number(s) you entered is out of range. Please enter a valid page number.\n
        Invalid page number(s) you entered: ${outOfRangePages.join(', ')}
      `
      "
      @close="pageOutOfRangeDialogOpened = false"
    />

    <WarningDialog
      :opened.sync="noPageClassifiedDialogOpened"
      warning-title="No Page Was Classified"
      warning-text="Please classify the pages using the auto classification tool or classify manually."
      @close="noPageClassifiedDialogOpened = false"
    />

    <ImagePreview
      :opened.sync="imagePreviewDialogOpened"
      :src="imagePreviewSrc"
    />

    <ConfirmationDialog
      :opened.sync="autoClassifyConfirmationDialogOpened"
      @cancel="autoClassifyConfirmationDialogOpened = false"
      @proceed="autoClassify"
      title="Before you continue"
      text="This feature may make errors. Although accuracy will improve over time, it's important to review and adjust the classification of each page. Do you wish to proceed?"
    />

    <AddPaperFiledReturnDialog
      :opened="addPaperFiledReturnDialogOpened"
      @cancel="addPaperFiledReturnDialogOpened = false"
      @proceed="handleAddPaperFiledReturn"
    />

    <!-- Partial: Alerts -->
    <sc-alert v-if="taxReturn.status === 'action_required'" type="info">
      <b>Instruction:</b> Please review the pages and add the correct pages
      under their respective classification on the right sidebar.
      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          @click="submit"
          icon-right="arrow-right"
          :is-loading="submitting"
          :is-disabled="pdfLoading || autoClassifying"
          data-test-id="delivery-next-classifications"
        >
          Next
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <ReadonlyAlert
      v-else
      :tax-return-status="taxReturn.status"
      :loading="submitting"
      :disabled="pdfLoading"
      @next="next"
    />

    <sc-flex>
      <aside
        class="sidebar left-sidebar hidden-sm-and-down"
        v-if="bookmarks.length > 0"
      >
        <v-card class="sidebar-card">
          <v-layout class="mx-3 mt-3 mb-2">
            <sc-h3 class="index-title">Index</sc-h3>
          </v-layout>

          <v-flex class="ma-3">
            The index is generated from the PDF's bookmarks and the auto
            classification tool. You can click on a page to navigate to it.
          </v-flex>

          <v-list dense expand class="pb-0 pt-0">
            <template v-for="(bookmark, index) in bookmarks">
              <v-list-group
                v-if="bookmark.items && bookmark.items.length > 0"
                :key="bookmark.title + ' ' + index"
                :id="`bookmark-${bookmark.pageIndex}`"
              >
                <template v-slot:activator>
                  <v-list-tile
                    :style="
                      pageWithinBookmark(bookmark)
                        ? 'background-color: var(--blue-100)'
                        : ''
                    "
                  >
                    <v-list-tile-content>
                      <v-list-tile-title style="text-decoration: underline">{{
                        bookmark.title
                      }}</v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </template>
                <v-list
                  dense
                  expand
                  class="pl-0 ml-3 text-align-left pt-0 pb-0"
                >
                  <template v-for="(subItem, subIndex) in bookmark.items">
                    <v-list-group
                      v-if="subItem.items && subItem.items.length > 0"
                      :key="subItem.title + ' ' + subIndex"
                    >
                      <template v-slot:activator>
                        <v-list-tile>
                          <v-list-tile-content>
                            <v-list-tile-title>
                              <v-layout justify-space-between align-center>
                                <span>{{ subItem.title }}</span>
                              </v-layout>
                            </v-list-tile-title>
                          </v-list-tile-content>
                        </v-list-tile>
                      </template>
                      <v-list
                        dense
                        expand
                        class="pl-0 ml-3 text-align-left pt-0 pb-0"
                      >
                        <template
                          v-for="(thirdItem, thirdIndex) in subItem.items"
                        >
                          <v-list-group
                            v-if="thirdItem.items && thirdItem.items.length > 0"
                            :key="thirdItem.title + ' ' + thirdIndex"
                          >
                            <template v-slot:activator>
                              <v-list-tile>
                                <v-list-tile-content>
                                  <v-list-tile-title>
                                    <v-layout
                                      justify-space-between
                                      align-center
                                    >
                                      <span>{{ thirdItem.title }}</span>
                                    </v-layout>
                                  </v-list-tile-title>
                                </v-list-tile-content>
                              </v-list-tile>
                            </template>
                            <v-list
                              dense
                              expand
                              class="pl-0 ml-3 text-align-left pt-0 pb-0"
                            >
                              <template
                                v-for="(fourthItem,
                                fourthIndex) in thirdItem.items"
                              >
                                <v-list-group
                                  v-if="
                                    fourthItem.items &&
                                      fourthItem.items.length > 0
                                  "
                                  :key="fourthItem.title + ' ' + fourthIndex"
                                >
                                  <template v-slot:activator>
                                    <v-list-tile>
                                      <v-list-tile-content>
                                        <v-list-tile-title>
                                          <v-layout
                                            justify-space-between
                                            align-center
                                          >
                                            <span>{{ fourthItem.title }}</span>
                                          </v-layout>
                                        </v-list-tile-title>
                                      </v-list-tile-content>
                                    </v-list-tile>
                                  </template>
                                  <v-list-tile
                                    v-for="fifthItem in fourthItem.items"
                                    :key="
                                      fifthItem.title +
                                        ' ' +
                                        fifthItem.pageIndex
                                    "
                                    @click="goToPage(fifthItem.pageIndex)"
                                    :style="
                                      selectedThumbnail === fifthItem.pageIndex
                                        ? 'background-color: var(--blue-100)'
                                        : {}
                                    "
                                    class="pl-4"
                                  >
                                    <v-list-tile-title>
                                      <v-layout
                                        justify-space-between
                                        align-center
                                      >
                                        <v-layout justify-start align-center>
                                          <div
                                            v-if="
                                              classificationForPage(
                                                fifthItem.pageIndex,
                                              ).includes('filing_instructions')
                                            "
                                            :style="
                                              `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                                'filing_instructions',
                                              )}`
                                            "
                                          ></div>
                                          <div
                                            v-if="
                                              classificationForPage(
                                                fifthItem.pageIndex,
                                              ).includes('payment_vouchers')
                                            "
                                            :style="
                                              `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                                'payment_vouchers',
                                              )}`
                                            "
                                          ></div>
                                          <div
                                            v-if="
                                              classificationForPage(
                                                fifthItem.pageIndex,
                                              ).includes('e_sign_forms')
                                            "
                                            :style="
                                              `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                                'e_sign_forms',
                                              )}`
                                            "
                                          ></div>
                                          <div
                                            v-if="
                                              classificationForPage(
                                                fifthItem.pageIndex,
                                              ).includes('tax_return_pages')
                                            "
                                            :style="
                                              `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                                'tax_return_pages',
                                              )}`
                                            "
                                          ></div>
                                          <div
                                            v-if="
                                              classificationForPage(
                                                fifthItem.pageIndex,
                                              ).includes('transmittal_letters')
                                            "
                                            :style="
                                              `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                                'transmittal_letters',
                                              )}`
                                            "
                                          ></div>
                                          <div
                                            v-if="
                                              classificationForPage(
                                                fifthItem.pageIndex,
                                              ).includes('paper_filed_returns')
                                            "
                                            :style="
                                              `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                                'paper_filed_returns',
                                              )}`
                                            "
                                          ></div>
                                          <template
                                            v-if="
                                              isOverflowing(
                                                'fifthItemTitle' + fifthIndex,
                                              )
                                            "
                                          >
                                            <v-tooltip bottom>
                                              <template
                                                v-slot:activator="{ on, attrs }"
                                              >
                                                <span
                                                  class="ellipsis"
                                                  v-on="on"
                                                  v-bind="attrs"
                                                  :ref="
                                                    'fifthItemTitle' +
                                                      fifthIndex
                                                  "
                                                  >{{ fifthItem.title }}</span
                                                >
                                              </template>
                                              <span>{{ fifthItem.title }}</span>
                                            </v-tooltip>
                                          </template>
                                          <template v-else>
                                            <span
                                              class="ellipsis"
                                              :ref="
                                                'fifthItemTitle' + fifthIndex
                                              "
                                              >{{ fifthItem.title }}</span
                                            >
                                          </template>
                                        </v-layout>
                                      </v-layout>
                                    </v-list-tile-title>
                                  </v-list-tile>
                                </v-list-group>
                                <v-list-tile
                                  v-else-if="fourthItem.pageIndex"
                                  :key="
                                    fourthItem.title +
                                      ' ' +
                                      fourthItem.pageIndex
                                  "
                                  @click="goToPage(fourthItem.pageIndex)"
                                  :style="
                                    selectedThumbnail === fourthItem.pageIndex
                                      ? 'background-color: var(--blue-100)'
                                      : {}
                                  "
                                  class="pl-4"
                                >
                                  <v-list-tile-title>
                                    <v-layout
                                      justify-space-between
                                      align-center
                                    >
                                      <v-layout justify-start align-center>
                                        <div
                                          v-if="
                                            classificationForPage(
                                              fourthItem.pageIndex,
                                            ).includes('filing_instructions')
                                          "
                                          :style="
                                            `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                              'filing_instructions',
                                            )}`
                                          "
                                        ></div>
                                        <div
                                          v-if="
                                            classificationForPage(
                                              fourthItem.pageIndex,
                                            ).includes('payment_vouchers')
                                          "
                                          :style="
                                            `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                              'payment_vouchers',
                                            )}`
                                          "
                                        ></div>
                                        <div
                                          v-if="
                                            classificationForPage(
                                              fourthItem.pageIndex,
                                            ).includes('e_sign_forms')
                                          "
                                          :style="
                                            `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                              'e_sign_forms',
                                            )}`
                                          "
                                        ></div>
                                        <div
                                          v-if="
                                            classificationForPage(
                                              fourthItem.pageIndex,
                                            ).includes('tax_return_pages')
                                          "
                                          :style="
                                            `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                              'tax_return_pages',
                                            )}`
                                          "
                                        ></div>
                                        <div
                                          v-if="
                                            classificationForPage(
                                              fourthItem.pageIndex,
                                            ).includes('transmittal_letters')
                                          "
                                          :style="
                                            `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                              'transmittal_letters',
                                            )}`
                                          "
                                        ></div>
                                        <div
                                          v-if="
                                            classificationForPage(
                                              fourthItem.pageIndex,
                                            ).includes('paper_filed_returns')
                                          "
                                          :style="
                                            `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                              'paper_filed_returns',
                                            )}`
                                          "
                                        ></div>
                                        <template
                                          v-if="
                                            isOverflowing(
                                              'fourthItemTitle' + fourthIndex,
                                            )
                                          "
                                        >
                                          <v-tooltip bottom>
                                            <template
                                              v-slot:activator="{ on, attrs }"
                                            >
                                              <span
                                                class="ellipsis"
                                                v-on="on"
                                                v-bind="attrs"
                                                :ref="
                                                  'fourthItemTitle' +
                                                    fourthIndex
                                                "
                                                >{{ fourthItem.title }}</span
                                              >
                                            </template>
                                            <span>{{ fourthItem.title }}</span>
                                          </v-tooltip>
                                        </template>
                                        <template v-else>
                                          <span
                                            class="ellipsis"
                                            :ref="
                                              'fourthItemTitle' + fourthIndex
                                            "
                                            >{{ fourthItem.title }}</span
                                          >
                                        </template>
                                      </v-layout>
                                    </v-layout>
                                  </v-list-tile-title>
                                </v-list-tile>
                              </template>
                            </v-list>
                          </v-list-group>
                          <v-list-tile
                            v-else-if="thirdItem.pageIndex"
                            :key="thirdItem.title + ' ' + thirdItem.pageIndex"
                            @click="goToPage(thirdItem.pageIndex)"
                            :style="
                              selectedThumbnail === thirdItem.pageIndex
                                ? 'background-color: var(--blue-100)'
                                : {}
                            "
                            class="pl-4"
                          >
                            <v-list-tile-title>
                              <v-layout justify-space-between align-center>
                                <v-layout justify-start align-center>
                                  <div
                                    v-if="
                                      classificationForPage(
                                        thirdItem.pageIndex,
                                      ).includes('filing_instructions')
                                    "
                                    :style="
                                      `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                        'filing_instructions',
                                      )}`
                                    "
                                  ></div>
                                  <div
                                    v-if="
                                      classificationForPage(
                                        thirdItem.pageIndex,
                                      ).includes('payment_vouchers')
                                    "
                                    :style="
                                      `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                        'payment_vouchers',
                                      )}`
                                    "
                                  ></div>
                                  <div
                                    v-if="
                                      classificationForPage(
                                        thirdItem.pageIndex,
                                      ).includes('e_sign_forms')
                                    "
                                    :style="
                                      `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                        'e_sign_forms',
                                      )}`
                                    "
                                  ></div>
                                  <div
                                    v-if="
                                      classificationForPage(
                                        thirdItem.pageIndex,
                                      ).includes('tax_return_pages')
                                    "
                                    :style="
                                      `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                        'tax_return_pages',
                                      )}`
                                    "
                                  ></div>
                                  <div
                                    v-if="
                                      classificationForPage(
                                        thirdItem.pageIndex,
                                      ).includes('transmittal_letters')
                                    "
                                    :style="
                                      `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                        'transmittal_letters',
                                      )}`
                                    "
                                  ></div>
                                  <div
                                    v-if="
                                      classificationForPage(
                                        thirdItem.pageIndex,
                                      ).includes('paper_filed_returns')
                                    "
                                    :style="
                                      `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                        'paper_filed_returns',
                                      )}`
                                    "
                                  ></div>
                                  <template
                                    v-if="
                                      isOverflowing(
                                        'thirdItemTitle' + thirdIndex,
                                      )
                                    "
                                  >
                                    <v-tooltip bottom>
                                      <template
                                        v-slot:activator="{ on, attrs }"
                                      >
                                        <span
                                          class="ellipsis"
                                          v-on="on"
                                          v-bind="attrs"
                                          :ref="'thirdItemTitle' + thirdIndex"
                                          >{{ thirdItem.title }}</span
                                        >
                                      </template>
                                      <span>{{ thirdItem.title }}</span>
                                    </v-tooltip>
                                  </template>
                                  <template v-else>
                                    <span
                                      class="ellipsis"
                                      :ref="'thirdItemTitle' + thirdIndex"
                                      >{{ thirdItem.title }}</span
                                    >
                                  </template>
                                </v-layout>
                              </v-layout>
                            </v-list-tile-title>
                          </v-list-tile>
                        </template>
                      </v-list>
                    </v-list-group>
                    <v-list-tile
                      v-else-if="subItem.pageIndex"
                      :key="subItem.title + ' ' + subItem.pageIndex"
                      @click="goToPage(subItem.pageIndex)"
                      :style="
                        selectedThumbnail === subItem.pageIndex
                          ? 'background-color: var(--blue-100)'
                          : {}
                      "
                      class="pl-4"
                    >
                      <v-list-tile-title>
                        <v-layout justify-space-between align-center>
                          <v-layout justify-start align-center>
                            <div
                              v-if="
                                classificationForPage(
                                  subItem.pageIndex,
                                ).includes('filing_instructions')
                              "
                              :style="
                                `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                  'filing_instructions',
                                )}`
                              "
                            ></div>
                            <div
                              v-if="
                                classificationForPage(
                                  subItem.pageIndex,
                                ).includes('payment_vouchers')
                              "
                              :style="
                                `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                  'payment_vouchers',
                                )}`
                              "
                            ></div>
                            <div
                              v-if="
                                classificationForPage(
                                  subItem.pageIndex,
                                ).includes('e_sign_forms')
                              "
                              :style="
                                `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                  'e_sign_forms',
                                )}`
                              "
                            ></div>
                            <div
                              v-if="
                                classificationForPage(
                                  subItem.pageIndex,
                                ).includes('tax_return_pages')
                              "
                              :style="
                                `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                  'tax_return_pages',
                                )}`
                              "
                            ></div>
                            <div
                              v-if="
                                classificationForPage(
                                  subItem.pageIndex,
                                ).includes('transmittal_letters')
                              "
                              :style="
                                `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                  'transmittal_letters',
                                )}`
                              "
                            ></div>
                            <div
                              v-if="
                                classificationForPage(
                                  subItem.pageIndex,
                                ).includes('paper_filed_returns')
                              "
                              :style="
                                `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                  'paper_filed_returns',
                                )}`
                              "
                            ></div>
                            <template
                              v-if="isOverflowing('subItemTitle' + subIndex)"
                            >
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <span
                                    class="ellipsis"
                                    v-on="on"
                                    v-bind="attrs"
                                    :ref="'subItemTitle' + subIndex"
                                    >{{ subItem.title }}</span
                                  >
                                </template>
                                <span>{{ subItem.title }}</span>
                              </v-tooltip>
                            </template>
                            <template v-else>
                              <span
                                class="ellipsis"
                                :ref="'subItemTitle' + subIndex"
                                >{{ subItem.title }}</span
                              >
                            </template>
                          </v-layout>
                        </v-layout>
                      </v-list-tile-title>
                    </v-list-tile>
                  </template>
                </v-list>
              </v-list-group>
              <v-list-tile
                v-else-if="bookmark.pageIndex"
                :key="bookmark.title + ' ' + index + 'no items'"
                :id="`bookmark-${bookmark.pageIndex}`"
                @click="goToPage(bookmark.pageIndex)"
                :style="
                  selectedThumbnail === bookmark.pageIndex
                    ? 'background-color: var(--blue-100)'
                    : ''
                "
              >
                <v-list-tile-title>
                  <v-layout align-center>
                    <div
                      v-if="
                        classificationForPage(bookmark.pageIndex).includes(
                          'filing_instructions',
                        )
                      "
                      :style="
                        `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                          'filing_instructions',
                        )}`
                      "
                    ></div>
                    <div
                      v-if="
                        classificationForPage(bookmark.pageIndex).includes(
                          'payment_vouchers',
                        )
                      "
                      :style="
                        `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                          'payment_vouchers',
                        )}`
                      "
                    ></div>
                    <div
                      v-if="
                        classificationForPage(bookmark.pageIndex).includes(
                          'e_sign_forms',
                        )
                      "
                      :style="
                        `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                          'e_sign_forms',
                        )}`
                      "
                    ></div>
                    <div
                      v-if="
                        classificationForPage(bookmark.pageIndex).includes(
                          'tax_return_pages',
                        )
                      "
                      :style="
                        `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                          'tax_return_pages',
                        )}`
                      "
                    ></div>
                    <div
                      v-if="
                        classificationForPage(bookmark.pageIndex).includes(
                          'transmittal_letters',
                        )
                      "
                      :style="
                        `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                          'transmittal_letters',
                        )}`
                      "
                    ></div>
                    <div
                      v-if="
                        classificationForPage(bookmark.pageIndex).includes(
                          'paper_filed_returns',
                        )
                      "
                      :style="
                        `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                          'paper_filed_returns',
                        )}`
                      "
                    ></div>
                    <template v-if="isOverflowing('bookmarkTitle' + index)">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            class="ellipsis"
                            :style="
                              bookmark.title.startsWith('Page')
                                ? 'width: 50px;'
                                : ''
                            "
                            v-on="on"
                            v-bind="attrs"
                            :ref="'bookmarkTitle' + index"
                            >{{ bookmark.title }}</span
                          >
                        </template>
                        <span>{{ bookmark.title }}</span>
                      </v-tooltip>
                    </template>
                    <template v-else>
                      <span
                        class="ellipsis"
                        :style="
                          bookmark.title.startsWith('Page')
                            ? 'width: 50px;'
                            : ''
                        "
                        :ref="'bookmarkTitle' + index"
                        >{{ bookmark.title }}</span
                      >
                    </template>
                    <v-tooltip
                      bottom
                      v-if="
                        guessedPages.find(
                          page => page.page === bookmark.pageIndex.toString(),
                        ) && bookmark.title.startsWith('Page')
                      "
                    >
                      <v-chip
                        slot="activator"
                        small
                        class="my-0"
                        style="max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                        >{{
                          guessedPages.find(
                            page => page.page === bookmark.pageIndex.toString(),
                          ).form_name
                        }}</v-chip
                      >
                      <span
                        >AI generated form name:<br />{{
                          guessedPages.find(
                            page => page.page === bookmark.pageIndex.toString(),
                          ).form_name
                        }}</span
                      >
                    </v-tooltip>
                  </v-layout>
                </v-list-tile-title>
              </v-list-tile>
            </template>
          </v-list>
        </v-card>
      </aside>

      <v-layout column class="scroll-container">
        <v-alert
          v-if="isSelectionModeOn"
          :value="isSelectionModeOn"
          outline
          type="info"
          class="ma-0 selection-banner py-2"
        >
          <v-layout align-center justify-space-between>
            <span class="body-1">
              You are currently in <b>Selection Mode</b>. Please click on the
              thumbnails to select pages for
              <b>{{
                selectedSection.hasSubSections
                  ? selectedSection.text + ` (${selectedSubSectionKey})`
                  : selectedSection.text
              }}</b
              >.
            </span>
            <v-btn
              color="primary"
              class="text-none pa-0 ma-0"
              @click="selectedSectionKey = null"
            >
              <b class="white--text">Done</b>
            </v-btn>
          </v-layout>
        </v-alert>

        <!-- Partial: Content -->
        <RecycleScroller
          ref="scroller"
          :items="sortedThumbnails"
          :item-size="thumbnailContainerMinHeight"
          :item-secondary-size="thumbnailContainerWidth"
          :buffer="thumbnailContainerMinHeight * 2"
          class="pdf-preview"
          key-field="page"
          v-slot="{ item }"
          :gridItems="gridItems"
        >
          <v-layout
            justify-center
            align-center
            v-if="pdfLoading"
            class="loading-container"
          >
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-layout>

          <div v-else class="thumbnail-container">
            <v-layout align-center justify-space-between class="thumbnail-top">
              <sc-flex :gap="2" :is-y-centered="true">
                <sc-small>Page {{ item.page }}</sc-small>
                <v-btn flat icon class="zoom-btn" @click="zoomIn(item)">
                  <v-icon>zoom_in</v-icon>
                </v-btn>
              </sc-flex>

              <sc-small>
                <v-menu offset-y v-if="getPageSections(item.page).length === 0">
                  <v-chip
                    slot="activator"
                    small
                    color="lightgrey"
                    text-color="darkgrey"
                  >
                    Unclassified
                    <v-icon color="darkgrey">arrow_drop_down</v-icon>
                  </v-chip>

                  <v-list class="select-section-list">
                    <v-layout
                      v-for="section in sections"
                      :key="section.key"
                      v-if="!section.hidden"
                    >
                      <v-layout v-if="section.hasSubSections" column>
                        <v-list-tile
                          v-for="subSectionKey in getSelectableSubSections(
                            section.key,
                          )"
                          :key="subSectionKey"
                          @click="
                            changePageSection(
                              item.page,
                              undefined,
                              section.key,
                              undefined,
                              subSectionKey,
                            )
                          "
                          :disabled="taxReturn.status !== 'action_required'"
                        >
                          <v-list-tile-title>{{
                            section.text + ` (${subSectionKey})`
                          }}</v-list-tile-title>
                        </v-list-tile>
                      </v-layout>

                      <v-list-tile
                        v-else
                        @click="
                          changePageSection(item.page, undefined, section.key)
                        "
                        :disabled="taxReturn.status !== 'action_required'"
                      >
                        <v-list-tile-title>{{
                          section.text
                        }}</v-list-tile-title>
                      </v-list-tile>
                    </v-layout>
                  </v-list>
                </v-menu>

                <v-menu
                  v-else
                  offset-y
                  v-for="{
                    section: selectedSection,
                    subSectionKey: selectedSubSectionKey,
                  } in getPageSections(item.page)"
                  :key="selectedSection.key"
                >
                  <v-chip
                    slot="activator"
                    small
                    :color="selectedSection.color"
                    text-color="white"
                  >
                    {{
                      selectedSection.hasSubSections
                        ? selectedSection.text + ` (${selectedSubSectionKey})`
                        : selectedSection.text
                    }}
                    <v-icon color="white">arrow_drop_down</v-icon>
                  </v-chip>

                  <v-list class="select-section-list">
                    <v-layout
                      v-for="section in sections"
                      v-if="!section.hidden"
                      :key="section.key"
                    >
                      <v-layout v-if="section.hasSubSections" column>
                        <v-list-tile
                          v-for="subSectionKey in getSelectableSubSections(
                            section.key,
                          )"
                          :disabled="
                            (selectedSection.key === section.key &&
                              selectedSubSectionKey === subSectionKey) ||
                              taxReturn.status !== 'action_required'
                          "
                          @click="
                            changePageSection(
                              item.page,
                              selectedSection.key,
                              section.key,
                              selectedSubSectionKey,
                              subSectionKey,
                            )
                          "
                        >
                          <v-list-tile-action>
                            <v-icon
                              v-if="
                                selectedSection.key === section.key &&
                                  selectedSubSectionKey === subSectionKey
                              "
                              color="success"
                              >check</v-icon
                            >
                          </v-list-tile-action>

                          <v-list-tile-content>
                            <v-list-tile-title>{{
                              section.text + ` (${subSectionKey})`
                            }}</v-list-tile-title>
                          </v-list-tile-content>
                        </v-list-tile>
                      </v-layout>

                      <v-list-tile
                        v-else
                        :disabled="taxReturn.status !== 'action_required'"
                        @click="
                          selectedSection.key === section.key
                            ? changeToUnclassify(
                                item.page,
                                selectedSection.key,
                                selectedSubSectionKey,
                              )
                            : changePageSection(
                                item.page,
                                selectedSection.key,
                                section.key,
                                selectedSubSectionKey,
                              )
                        "
                      >
                        <v-list-tile-action>
                          <v-icon
                            v-if="selectedSection.key === section.key"
                            color="success"
                            >check</v-icon
                          >
                        </v-list-tile-action>

                        <v-list-tile-content>
                          <v-list-tile-title>{{
                            section.text
                          }}</v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                    </v-layout>
                  </v-list>
                </v-menu>
              </sc-small>
            </v-layout>

            <div
              class="image-container ph-no-capture"
              :style="
                selectedThumbnail === item.page
                  ? { border: '2px solid var(--blue-700)' }
                  : { border: '2px solid transparent' }
              "
              @click="selectThumbnail(item.page)"
            >
              <div v-if="!item.loaded" class="thumbnail thumbnail-placeholder">
                <span>Loading...</span>
              </div>

              <img
                :src="item.thumbnail"
                @load="item.loaded = true"
                v-show="item.loaded"
                :id="`thumbnail-page-${item.page}`"
                class="thumbnail"
                @click="isSelectionModeOn ? toggleSelect(item.page) : null"
                @dblclick="zoomIn(item)"
                :class="{
                  selected: selectedPages.includes(item.page),
                  selectable: isSelectionModeOn,
                  [`thumbnail-page-${item.page}`]: true,
                }"
              />

              <div
                v-if="watermarkedPages.includes(item.page)"
                class="watermark"
                :style="getWatermarkStyle(item.page)"
              >
                {{ watermark }}
              </div>
            </div>
          </div>
        </RecycleScroller>
      </v-layout>

      <!-- Partial: Sidebar -->
      <aside class="sidebar right-sidebar">
        <v-layout column class="autoclassify">
          <v-tooltip bottom>
            <v-btn
              v-if="!autoClassifying"
              class="auto-classify-btn text-none"
              :is-disabled="thumbnails.length === 0 || !editable"
              :is-loading="autoClassifying"
              @click="openAutoClassifyConfirmationDialog()"
              slot="activator"
              block
              data-test-id="auto-classify-button"
              >Auto classify</v-btn
            >
            <span>
              Auto-classify uncategorized pages.
            </span>
          </v-tooltip>

          <v-btn
            v-if="autoClassifying"
            class="text-none cancel-btn mx-0"
            @click="cancelAutoClassify()"
          >
            <span>Cancel</span>
          </v-btn>

          <v-layout align-center justify-start class="pt-2">
            <v-checkbox
              v-model="showOnlyUncategorized"
              @change="flipFilters"
              color="primary"
              hide-details
              class="ma-0 pa-0"
              data-test-id="show-only-uncategorized-checkbox"
            >
              <template v-slot:label>
                <sc-small>
                  Show only uncategorized pages ({{
                    allPagesCount - classifiedPagesCount
                  }})
                </sc-small>
              </template>
            </v-checkbox>
          </v-layout>

          <v-layout column class="pt-2" v-if="estimatedOcrTime">
            <div class="body-1">
              Please hang on tight while we process this tax return for
              auto-classification. You can exit and return later. The estimated
              wait time is
              <b class="red--text text--darken-2">
                {{
                  formatEstimatedTime(
                    estimatedOcrTime + estimatedClassificationTime,
                  )
                }}.
              </b>
            </div>
          </v-layout>

          <v-layout column class="pt-2" v-else-if="estimatedClassificationTime">
            <div v-if="autoClassifying" class="body-1">
              The estimated time to complete auto-classification is
              <b>{{ formatEstimatedTime(estimatedClassificationTime) }}.</b>
            </div>

            <div v-else class="body-1">
              This tax return is ready for auto-classification.
            </div>
          </v-layout>

          <v-layout v-if="autoClassifying" class="py-2">
            <v-progress-linear
              background-color="grey lighten-2"
              class="my-0"
              v-model="classificationProgress"
            />
          </v-layout>
        </v-layout>

        <div
          v-for="section in sections.filter(section => !section.hidden)"
          :key="section.key"
          class="sidebar__content"
        >
          <sc-flex :is-column="true" :gap="2">
            <v-layout align-center justify-start class="pb-2">
              <sc-h4>
                <span :style="`font-weight: 600; color: ${section.color};`">
                  {{ section.text }}
                </span>
              </sc-h4>
              <v-spacer />

              <v-tooltip bottom>
                <v-btn
                  outline
                  fab
                  small
                  :color="currentFilter === section.key ? 'primary' : 'grey'"
                  @click="toggleSectionVisibility(section.key)"
                  slot="activator"
                  class="mr-1 icon-btn"
                >
                  <v-icon class="material-icons-outlined">
                    visibility
                  </v-icon>
                </v-btn>

                <span>Show only {{ section.text }}</span>
              </v-tooltip>
            </v-layout>

            <sc-small>{{ section.description }}</sc-small>
          </sc-flex>

          <sc-flex
            :is-column="true"
            :gap="16"
            class="pt-2"
            v-if="typeof pageRangesBySection[section.key] === 'object'"
          >
            <sc-flex
              v-for="[subSectionKey, pageRange] in toPairs(
                pageRangesBySection[section.key],
              )"
              :key="subSectionKey"
              :is-column="true"
            >
              <v-layout justify-space-between align-center>
                <sc-h6>Pages for {{ subSectionKey }}:</sc-h6>

                <v-btn
                  icon
                  flat
                  small
                  color="error"
                  :disabled="!editable"
                  @click="deletePaperFiledReturnPackage(subSectionKey)"
                >
                  <v-icon small>delete</v-icon>
                </v-btn>
              </v-layout>

              <input
                class="sidebar__input"
                :class="{
                  selected:
                    selectedSectionKey === section.key &&
                    selectedSubSectionKey === subSectionKey,
                }"
                :placeholder="pdfLoading ? 'Loading...' : 'Example: 1-5, 7'"
                :disabled="!editable"
                :value="
                  autoClassifying
                    ? 'Auto classification in progress...'
                    : pageRangesBySection[section.key][subSectionKey]
                "
                @input="
                  !autoClassifying &&
                    $set(
                      pageRangesBySection[section.key],
                      subSectionKey,
                      $event.target.value,
                    )
                "
                @focus="selectSection(section.key, subSectionKey)"
              />
            </sc-flex>
          </sc-flex>

          <sc-flex :is-column="true" :gap="8" class="pt-2" v-else>
            <sc-h6>Pages:</sc-h6>
            <input
              class="sidebar__input"
              :class="{ selected: selectedSectionKey === section.key }"
              :placeholder="pdfLoading ? 'Loading...' : 'Example: 1-5, 7'"
              :disabled="!editable"
              :value="
                autoClassifying
                  ? 'Auto classification in progress...'
                  : pageRangesBySection[section.key]
              "
              @input="
                !autoClassifying &&
                  $set(pageRangesBySection, section.key, $event.target.value)
              "
              @focus="selectSection(section.key)"
            />
          </sc-flex>

          <sc-flex v-if="section.key === 'tax_return_pages'" :is-column="true">
            <v-checkbox
              class="mt-1"
              hide-details
              v-model="taxReturn.watermark_enabled"
              color="primary"
              :disabled="!editable"
              @click.native.stop.prevent=""
            >
              <template v-slot:label>
                <sc-small>Add watermark ({{ watermark }})</sc-small>
              </template>
            </v-checkbox>
          </sc-flex>

          <sc-flex
            v-if="section.key === 'paper_filed_returns'"
            :is-column="true"
          >
            <v-btn
              color="primary"
              flat
              class="text-none py-1"
              block
              @click.native.stop.prevent="addPaperFiledReturnPackage()"
              :disabled="!editable"
            >
              <v-icon size="medium">add</v-icon>
              Add packages
            </v-btn>
          </sc-flex>
        </div>
      </aside>
    </sc-flex>
  </sc-flex>
</template>

<script>
import {
  generatePdfThumbnails,
  PDFJS_CMAP_URL,
  PDFJS_STANDARD_FONT_DATA_URL,
  PDFJS_WORKER_SRC,
} from '@/utils/pdf';
import { PDFDocument } from 'pdf-lib';
import API from '@/api';
import uniq from 'lodash/uniq';
import { mapGetters } from 'vuex';
import rangeParser from 'parse-numeric-range';
import axios from 'axios';
import mapValues from 'lodash/mapValues';
import ReadonlyAlert from '@/components/returns/ReadonlyAlert';
import WarningDialog from '@/components/returns/WarningDialog';
import SavingDialog from '@/components/returns/SavingDialog';
import ConfirmationDialog from '@/components/returns/ConfirmationDialog';
import ImagePreview from '@/components/ImagePreview';
import AddPaperFiledReturnDialog from '@/components/returns/AddPaperFiledReturnDialog';
// import posthog from 'posthog-js';
import { v4 as uuid } from 'uuid';
import { Sema } from 'async-sema';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import fromPairs from 'lodash/fromPairs';
import toPairs from 'lodash/toPairs';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import { duration } from 'moment';
import * as Sentry from '@sentry/vue';

export default {
  components: {
    WarningDialog,
    SavingDialog,
    ReadonlyAlert,
    ConfirmationDialog,
    ImagePreview,
    AddPaperFiledReturnDialog,
  },

  mixins: [FirmPreferenceMixins],

  props: {
    currentCompany: {
      type: Object,
      default: () => {},
    },

    taxReturn: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      selectedThumbnail: null,
      bookmarks: [],
      pdfLoading: false,
      thumbnails: [],
      submitting: false,
      autoClassifying: false,
      autoClassifyConfirmationDialogOpened: false,

      classificationRequestId: null,

      sections: [],
      selectedSectionKey: null,
      selectedSubSectionKey: null,
      pageRangesBySection: {
        transmittal_letters: '',
        filing_instructions: '',
        tax_return_pages: '',
        e_sign_forms: '',
        paper_filed_returns: {},
        payment_vouchers: '',
      },

      imagePreviewDialogOpened: false,
      imagePreviewSrc: null,

      pageOutOfRangeDialogOpened: false,
      noPageClassifiedDialogOpened: false,
      addPaperFiledReturnDialogOpened: false,
      savingDialogOpened: false,

      estimatedOcrTime: null,
      getEstimatedWaitingTimeHandler: null,

      textPages: [],
      showOnlyUncategorized: false,
      currentFilter: 'none',
      guessedPages: [],

      // Thumbnail settings
      thumbnailContainerMinHeight: 480,
      thumbnailContainerMinWidth: 320,
      containerWidth: 1200,
      resizeObserver: null,

      watermark: 'DO NOT FILE',
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),

    isUsingCustomClassificationModel() {
      return (
        (this.currentCompany.entity_type === 'Individual' &&
          this.taxReturnIndividualCustomModelEnabled) ||
        (this.currentCompany.entity_type !== 'Individual' &&
          this.taxReturnBusinessCustomModelEnabled)
      );
    },

    estimatedClassificationTime() {
      if (this.isUsingCustomClassificationModel) {
        return null;
      }

      const throughPut = 8; // 8 images per second

      if (!this.taxReturn.original_images || this.estimatedOcrTime === null) {
        return 0;
      }

      const classifiedPages = this.classifiedPagesCount;
      const unclassifiedPages = this.allPagesCount - classifiedPages;

      const estimated = Math.ceil(unclassifiedPages / throughPut);

      return estimated;
    },

    classificationProgress() {
      return (this.classifiedPagesCount / this.allPagesCount) * 100;
    },

    editable() {
      return (
        this.taxReturn.status === 'action_required' &&
        !this.autoClassifying &&
        !this.submitting &&
        !this.pdfLoading
      );
    },

    pagesBySection() {
      return mapValues(this.pageRangesBySection, subSectionOrPageRanges => {
        if (typeof subSectionOrPageRanges === 'object') {
          return mapValues(subSectionOrPageRanges, pageRange =>
            rangeParser(pageRange),
          );
        } else {
          return rangeParser(subSectionOrPageRanges);
        }
      });
    },

    sortedThumbnails() {
      return this.thumbnails
        .sort((a, b) => a.page - b.page)
        .filter(thumbnail =>
          this.currentFilter !== 'none'
            ? this.classificationForPage(thumbnail.page).includes(
                this.currentFilter,
              )
            : !this.showOnlyUncategorized ||
              this.classificationForPage(thumbnail.page).length === 0,
        );
    },

    isSelectionModeOn() {
      return this.selectedSectionKey !== null;
    },

    selectedSection() {
      return this.sections.find(
        section => section.key === this.selectedSectionKey,
      );
    },

    selectedPages() {
      if (!this.selectedSection) {
        return [];
      }

      if (this.selectedSection.hasSubSections) {
        return this.pagesBySection[this.selectedSectionKey][
          this.selectedSubSectionKey
        ];
      } else {
        return this.pagesBySection[this.selectedSectionKey];
      }
    },

    watermarkedPages() {
      return this.taxReturn.watermark_enabled
        ? this.pagesBySection['tax_return_pages']
        : [];
    },

    outOfRangePages() {
      const maxPage = this.thumbnails.length;

      return uniq(
        Object.values(this.pagesBySection)
          .flatMap(pagesOrSubSections => {
            if (typeof pagesOrSubSections === 'object') {
              return Object.values(pagesOrSubSections).flat();
            } else {
              return pagesOrSubSections;
            }
          })
          .filter(page => {
            return page < 1 || page > maxPage;
          }),
      );
    },

    isNoneClassified() {
      return !Object.values(this.pagesBySection).some(pagesOrSubSections => {
        if (typeof pagesOrSubSections === 'object') {
          return Object.values(pagesOrSubSections).flat().length !== 0;
        } else {
          return pagesOrSubsections.length !== 0;
        }
      });
    },

    allPagesCount() {
      return this.thumbnails.length;
    },

    classifiedPagesCount() {
      const classifiedPages = Object.values(this.pagesBySection)
        .map(pagesOrSubSections => {
          if (typeof pagesOrSubSections === 'object') {
            return Object.values(pagesOrSubSections).flat();
          } else {
            return pagesOrSubsections;
          }
        })
        .flat();

      return uniq(classifiedPages).length;
    },
    bookmarksFlattened() {
      const flattenBookmarks = bookmarks => {
        let pages = [];
        bookmarks.forEach(bookmark => {
          if (bookmark.pageIndex) {
            // Check if bookmark title indicates multiple pages (e.g., "CA (59 pages)")
            const pageCountMatch = bookmark.title.match(/\((\d+)\s+pages?\)/);
            if (pageCountMatch) {
              const pageCount = parseInt(pageCountMatch[1]);
              // Add all pages covered by this bookmark
              for (let i = 0; i < pageCount; i++) {
                const pageIndex = bookmark.pageIndex + i;
                if (pageIndex <= this.thumbnails.length) {
                  pages.push(pageIndex);
                }
              }
            } else {
              pages.push(bookmark.pageIndex);
            }
          }
          if (bookmark.items && bookmark.items.length > 0) {
            pages = pages.concat(flattenBookmarks(bookmark.items));
          }
        });
        return pages;
      };
      return flattenBookmarks(this.bookmarks);
    },

    gridItems() {
      // Calculate the number of columns that can fit in the scroll container
      // Based on container width and thumbnail width (including padding)

      const thumbnailWidth = this.thumbnailContainerMinWidth;
      const minColumns = 1;
      const maxColumns = 6;

      const calculatedColumns = Math.floor(
        this.containerWidth / thumbnailWidth,
      );
      return Math.max(minColumns, Math.min(maxColumns, calculatedColumns));
    },

    thumbnailContainerWidth() {
      const columns = this.gridItems;

      const thumbnailWidth = Math.max(
        this.thumbnailContainerMinWidth,
        Math.floor(this.containerWidth / columns),
      );

      return thumbnailWidth;
    },
  },

  methods: {
    isEmpty,
    toPairs,

    updateContainerWidth() {
      const scrollContainer = document.querySelector('.scroll-container');
      this.containerWidth = scrollContainer
        ? scrollContainer.clientWidth
        : 1200;
    },

    setupResizeObserver() {
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateContainerWidth();
        });

        const scrollContainer = document.querySelector('.scroll-container');
        if (scrollContainer) {
          this.resizeObserver.observe(scrollContainer);
        }
      }
    },

    cleanupResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },

    colorForSection(section) {
      return this.sections.find(s => s.key === section).color;
    },

    flipFilters() {
      this.selectedThumbnail = null;
      this.currentFilter = 'none';
      this.sections.forEach(section => {
        section.show_filter = !this.showOnlyUncategorized;
      });
    },

    toggleSectionVisibility(sectionKey) {
      if (this.currentFilter === sectionKey) {
        this.currentFilter = 'none';
        this.sections.forEach(section => {
          section.show_filter = true;
        });
      } else {
        this.currentFilter = sectionKey;
        this.sections.find(
          section => section.key === sectionKey,
        ).show_filter = true;

        this.sections.forEach(section => {
          if (section.key !== sectionKey) {
            section.show_filter = false;
          }
        });
      }
    },

    selectThumbnail(page) {
      this.selectedThumbnail = page;
      this.scrollToBookmark(page);
    },

    scrollToBookmark(page) {
      const pageElement = document.querySelector(`#bookmark-${page + 2}`);
      if (pageElement) {
        // scroll so the page is in the center of the screen
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    formatEstimatedTime(seconds) {
      const units = [
        { label: 'hour', value: 3600 },
        { label: 'minute', value: 60 },
        { label: 'second', value: 1 },
      ];

      const time = [];

      units.forEach(unit => {
        const unitValue = Math.floor(seconds / unit.value);
        if (unitValue > 0) {
          time.push(`${unitValue} ${unit.label}${unitValue > 1 ? 's' : ''}`);
          seconds %= unit.value;
        }
      });

      return time.join(', ');
    },

    async loadThumbnails() {
      const response1 = await API.integration.downloadDocument({
        companyId: this.currentCompany.id,
        link: this.taxReturn.original_file,
      });
      const pdfSrc = response1.data.document.data.link;

      this.extractBookmarks(pdfSrc);

      // Fast path: show pre-rendered S3 images immediately, then re-render
      // in the background with correct font config to fix any broken pages.
      if (this.taxReturn.original_images) {
        try {
          const response = await API.taxReturn.getOriginalImages({
            id: this.taxReturn.id,
            companyId: this.currentCompany.id,
          });

          if (response.data.length > 0) {
            this.thumbnails = response.data.map(image => ({
              page: image.page,
              thumbnail: image.url,
              loaded: false,
            }));

            this.reRenderThumbnailsFromPdf(pdfSrc);
            return;
          }
        } catch (error) {
          console.error({ error });
        }
      }

      // Slow path: no S3 images available, render all pages client-side.
      const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pdfData = await pdfDoc.saveAsBase64({ dataUri: true });

      const generatedThumbnails = await generatePdfThumbnails(pdfData);
      this.thumbnails = generatedThumbnails.map(thumb => ({
        ...thumb,
        loaded: false,
      }));
    },

    async reRenderThumbnailsFromPdf(pdfSrc) {
      try {
        const pdflib = window.pdfjsLib;
        const loadingTask = pdflib.getDocument({
          url: pdfSrc,
          cMapUrl: PDFJS_CMAP_URL,
          cMapPacked: true,
          standardFontDataUrl: PDFJS_STANDARD_FONT_DATA_URL,
        });
        const pdf = await loadingTask.promise;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          await page.render({
            canvasContext: canvas.getContext('2d'),
            viewport,
          }).promise;

          const thumbnail = canvas.toDataURL();
          const existing = this.thumbnails.find(t => t.page === pageNum);
          if (existing) {
            existing.thumbnail = thumbnail;
            existing.loaded = false;
          }
        }
      } catch (error) {
        console.error('Error re-rendering PDF thumbnails:', error);
      }
    },

    validate() {
      if (this.outOfRangePages.length > 0) {
        this.pageOutOfRangeDialogOpened = true;
        return false;
      } else if (this.isNoneClassified) {
        this.noPageClassifiedDialogOpened = true;
        return false;
      }

      return true;
    },

    async save() {
      const response = await axios.post(
        `/tax_returns/${this.taxReturn.id}/classify`,
        {
          company_id: this.currentCompany.id,
          pages_by_section: this.pagesBySection,
          watermark_enabled: this.taxReturn.watermark_enabled,
          document_page_count: this.thumbnails.length,
        },
      );

      return response.data;
    },

    async submit() {
      if (!this.validate()) {
        return;
      }

      try {
        this.submitting = true;

        const taxReturn = await this.save();

        if (taxReturn) {
          this.$emit('classification-updated', taxReturn);
        }
      } finally {
        this.submitting = false;
      }
    },

    async showSavingDialog() {
      if (this.savingDialogOpened) {
        return;
      }

      try {
        this.savingDialogOpened = true;

        const taxReturn = await this.save();

        if (taxReturn) {
          this.$emit('classification-updated', taxReturn);
        }
      } finally {
        this.savingDialogOpened = false;
      }
    },

    async beforeClose() {
      // Before closing, we cancel any ongoing auto-classification so that we can save the classification progress
      if (this.autoClassifying) {
        this.cancelAutoClassify();
      }

      if (!this.editable) {
        return true;
      } else {
        await this.showSavingDialog();
        return true;
      }
    },

    selectSection(sectionKey, subSectionKey) {
      if (!this.editable) {
        return;
      }

      this.selectedSectionKey = sectionKey;
      this.selectedSubSectionKey = subSectionKey || null;
    },

    toggleSelect(page) {
      if (!this.isSelectionModeOn) {
        return;
      }

      let newSelectedPages = [];

      if (this.selectedPages.includes(page)) {
        newSelectedPages = this.selectedPages.filter(p => p !== page);
      } else {
        newSelectedPages = [...this.selectedPages, page];
      }

      // Handle the case for paper-filed returns that has sub-sections
      if (this.selectedSubSectionKey !== null) {
        this.$set(this.pageRangesBySection, this.selectedSectionKey, {
          ...this.pageRangesBySection[this.selectedSectionKey],

          [this.selectedSubSectionKey]: this.convertToRanges(
            newSelectedPages,
          ).join(', '),
        });
      } else {
        this.$set(
          this.pageRangesBySection,
          this.selectedSectionKey,
          this.convertToRanges(newSelectedPages).join(', '),
        );
      }
    },

    zoomIn(image) {
      this.imagePreviewSrc = image.thumbnail;
      this.imagePreviewDialogOpened = true;
    },

    next() {
      this.$emit('next');
    },

    getPageSections(page) {
      const items = [];

      this.sections.forEach(section => {
        const subSectionOrPageRanges = this.pageRangesBySection[section.key];

        // The case when the section has sub sections (like Paper-filed returns)
        if (typeof subSectionOrPageRanges === 'object') {
          toPairs(subSectionOrPageRanges).forEach(
            ([subSectionKey, pageRange]) => {
              if (rangeParser(pageRange).includes(page)) {
                items.push({
                  section,
                  subSectionKey,
                });
              }
            },
          );
        } else if (rangeParser(subSectionOrPageRanges).includes(page)) {
          items.push({ section });
        }
      });

      return items;
    },

    getSelectableSubSections(sectionKey) {
      const subSections = this.pageRangesBySection[sectionKey];

      return Object.keys(subSections);
    },

    /**
     * If any saved page is outside 1..thumbnailCount, returns a copy with those pages
     * removed; otherwise returns the original object (no work when already valid).
     */
    normalizeClassificationSectionsForThumbnails(sections, thumbnailCount) {
      if (!thumbnailCount || thumbnailCount < 1) {
        return sections;
      }

      const isValidPage = page => {
        const n = Number(page);
        return Number.isInteger(n) && n >= 1 && n <= thumbnailCount;
      };

      const hasInvalid = arr =>
        Array.isArray(arr) && arr.some(p => !isValidPage(p));

      const paper = sections.paper_filed_returns;
      const paperHasInvalid =
        paper &&
        typeof paper === 'object' &&
        !Array.isArray(paper) &&
        Object.keys(paper).some(name => hasInvalid(paper[name]));

      if (
        !hasInvalid(sections.transmittal_letters) &&
        !hasInvalid(sections.filing_instructions) &&
        !hasInvalid(sections.tax_return_pages) &&
        !hasInvalid(sections.e_sign_forms) &&
        !hasInvalid(sections.payment_vouchers) &&
        !paperHasInvalid
      ) {
        return sections;
      }

      const filterPageArray = arr =>
        Array.isArray(arr) ? arr.filter(isValidPage) : [];

      let paperFiltered = paper;
      if (paper && typeof paper === 'object' && !Array.isArray(paper)) {
        paperFiltered = {};
        Object.keys(paper).forEach(name => {
          const pages = filterPageArray(paper[name]);
          if (pages.length > 0) {
            paperFiltered[name] = pages;
          }
        });
      }

      return {
        transmittal_letters: filterPageArray(sections.transmittal_letters),
        filing_instructions: filterPageArray(sections.filing_instructions),
        tax_return_pages: filterPageArray(sections.tax_return_pages),
        e_sign_forms: filterPageArray(sections.e_sign_forms),
        payment_vouchers: filterPageArray(sections.payment_vouchers),
        paper_filed_returns: paperFiltered,
      };
    },

    setPageRangesFromSections(sections) {
      this.pageRangesBySection.transmittal_letters = Array.isArray(
        sections.transmittal_letters,
      )
        ? this.convertToRanges(sections.transmittal_letters).join(', ')
        : '';

      this.pageRangesBySection.filing_instructions = Array.isArray(
        sections.filing_instructions,
      )
        ? this.convertToRanges(sections.filing_instructions).join(', ')
        : '';

      this.pageRangesBySection.tax_return_pages = Array.isArray(
        sections.tax_return_pages,
      )
        ? this.convertToRanges(sections.tax_return_pages).join(', ')
        : '';

      this.pageRangesBySection.e_sign_forms = Array.isArray(
        sections.e_sign_forms,
      )
        ? this.convertToRanges(sections.e_sign_forms).join(', ')
        : '';

      this.pageRangesBySection.payment_vouchers = Array.isArray(
        sections.payment_vouchers,
      )
        ? this.convertToRanges(sections.payment_vouchers).join(', ')
        : '';

      this.pageRangesBySection.paper_filed_returns =
        typeof sections.paper_filed_returns === 'object'
          ? mapValues(sections.paper_filed_returns, pages =>
              this.convertToRanges(pages).join(', '),
            )
          : {};
    },

    isPageClassified(page) {
      return Object.values(this.pagesBySection).some(pagesOrSubSections => {
        if (typeof pagesOrSubSections === 'object') {
          return Object.values(pagesOrSubSections)
            .flat()
            .includes(page);
        } else {
          return pagesOrSubsections.includes(page);
        }
      });
    },

    convertToRanges(array) {
      const uniqSorted = uniq(array.sort((a, b) => a - b));
      const ranges = [];

      for (let i = 0; i < uniqSorted.length; i++) {
        let rangeStart = uniqSorted[i];
        let rangeEnd = uniqSorted[i];

        while (uniqSorted[i + 1] - uniqSorted[i] === 1) {
          rangeEnd = uniqSorted[i + 1];
          i++;
        }

        if (rangeStart === rangeEnd) ranges.push(rangeStart);
        else ranges.push(`${rangeStart}-${rangeEnd}`);
      }

      return ranges;
    },

    convertRangesToArray(rangeString) {
      if (!rangeString) return [];

      return rangeString.split(',').flatMap(range => {
        const [start, end] = range.split('-').map(Number);
        return end
          ? Array.from({ length: end - start + 1 }, (_, i) => start + i)
          : [start];
      });
    },

    async extractBookmarks(pdfSrc) {
      try {
        const pdflib = window.pdfjsLib;
        pdflib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;

        const loadingTask = pdflib.getDocument({
          url: pdfSrc,
          cMapUrl: PDFJS_CMAP_URL,
          cMapPacked: true,
          standardFontDataUrl: PDFJS_STANDARD_FONT_DATA_URL,
        });
        const pdf = await loadingTask.promise;

        this.extractBookmarksFromPdf(pdf);
      } catch (error) {
        console.error('Error extracting bookmarks:', error);
      }
    },

    async extractBookmarksFromPdf(pdf) {
      try {
        const outline = await pdf.getOutline();
        const numPages = pdf.numPages;

        if (!outline) {
          this.bookmarks = [];
        } else {
          const processBookmarks = async bookmarks => {
            const processed = await Promise.all(
              bookmarks.map(async bookmark => {
                let pageIndex = null;

                // Extract page index correctly from dest[0]
                if (
                  bookmark.dest &&
                  bookmark.dest.length > 0 &&
                  bookmark.dest[0]
                ) {
                  try {
                    const resolvedPageIndex = await pdf.getPageIndex(
                      bookmark.dest[0],
                    );
                    pageIndex = resolvedPageIndex + 1; // Convert to 1-based index
                  } catch (error) {
                    console.warn(
                      `Failed to resolve page index for: ${bookmark.title}`,
                      error,
                    );
                  }
                }

                return {
                  title: bookmark.title,
                  pageIndex: pageIndex,
                  items: bookmark.items
                    ? await processBookmarks(bookmark.items)
                    : [],
                };
              }),
            );

            return processed;
          };

          this.bookmarks = await processBookmarks(outline);

          if (this.bookmarks.length === 1) {
            this.bookmarks = this.bookmarks[0].items;
          }
        }

        this.insertSkippedPagesIntoBookmarks();

        this.textPages = [];

        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          const pageText = textContent.items
            .map(
              item => item.str,
              // `${item.str} ${Math.round(item.width)} ${item.transform
              //   .map(t => Math.round(t))
              //   .join(',')}`,
            )
            .join(' ');
          const bookmark = this.bookmarks.find(
            bookmark => bookmark.pageIndex === pageNum,
          );
          this.textPages.push({
            page: pageNum,
            text: pageText,
            bookmark: bookmark ? bookmark.title : null,
          });
        }
      } catch (error) {
        console.error('Error processing bookmarks:', error);
      }
    },

    pageWithinBookmark(bookmark) {
      return bookmark.items.some(
        item => item.pageIndex === this.selectedThumbnail,
      );
    },

    goToPage(pageIndex) {
      const itemIndex = this.sortedThumbnails.findIndex(
        thumbnail => thumbnail.page === pageIndex,
      );

      if (itemIndex !== -1 && this.$refs.scroller) {
        this.$refs.scroller.scrollToItem(itemIndex);
      }
    },

    insertSkippedPagesIntoBookmarks() {
      const allBookmarkedPages = this.bookmarksFlattened;
      for (let page = 1; page <= this.thumbnails.length; page++) {
        if (!allBookmarkedPages.includes(page)) {
          this.bookmarks.push({
            pageIndex: page,
            title: `Page ${page}`,
            items: [],
          });
        }
      }
      // Sort bookmarks by pageIndex
      this.bookmarks.sort((a, b) => a.pageIndex - b.pageIndex);
    },

    async autoClassify() {
      this.autoClassifyConfirmationDialogOpened = false;
      this.autoClassifying = true;
      this.selectedSectionKey = null;

      const requestId = uuid();
      this.classificationRequestId = requestId;

      const pageIndices = this.thumbnails.map((_, index) => index);

      const unclassifiedPageIndices = pageIndices.filter(
        index => !this.isPageClassified(index + 1),
      );

      const sema = new Sema(4); // Limit concurrency of classify tasks at a time

      // Create the classify tasks to be run in parallel
      const classifyTasks = unclassifiedPageIndices.map(async pageIndex => {
        const page = pageIndex + 1;

        await sema.acquire();

        try {
          if (requestId === this.classificationRequestId) {
            await this.classifyPage(page, requestId);
          }
        } finally {
          sema.release();
        }
      });

      await Promise.all(classifyTasks);

      if (requestId === this.classificationRequestId) {
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Auto classification completed.',
        });
        this.autoClassifying = false;

        // posthog.capture('auto classified pages', {
        //   company_id: this.currentCompany.id,
        // });
      }
    },

    async classifyPage(page, requestId) {
      try {
        if (this.isPageClassified(page)) {
          return;
        }
        const MAX_TEXT_LENGTH = 10000; // Adjust as needed

        let pageDetails = [];

        if (this.taxReturnUseNativePdfReaderForClassificationEnabled) {
          pageDetails = this.textPages.find(textPage => textPage.page === page);

          // Truncate the text if it's too long
          if (pageDetails && pageDetails.text.length > MAX_TEXT_LENGTH) {
            pageDetails.text =
              pageDetails.text.slice(0, MAX_TEXT_LENGTH) + '...'; // Append "..." to indicate truncation
          }
        }

        // Implement retry logic with a 1-minute timeout
        let response;
        let retries = 0;
        const maxRetries = 30; // 1 minute with 2-second intervals

        while (retries < maxRetries) {
          try {
            response = await axios.get(
              `/tax_returns/${this.taxReturn.id}/get_suggested_form_types`,
              {
                params: {
                  pages: [page], // Limited pages
                  page_details: pageDetails,
                  company_id: this.currentCompany.id,
                },
              },
            );
            break; // Success, exit the retry loop
          } catch (error) {
            // Only retry if we get a 425 Too Early response
            if (error.response && error.response.status === 425) {
              console.log('Got 425 Too Early response, retrying...');
              retries++;
              if (retries >= maxRetries) {
                console.error(
                  'Failed to get suggested form types after multiple retries:',
                  error,
                );
                Sentry.captureException(error);
                throw error; // Rethrow after max retries
              }
              // Wait 2 seconds before retrying
              await new Promise(resolve => setTimeout(resolve, 2000));
              continue;
            }

            // For any other error, don't retry
            console.error('Error getting suggested form types:', error);
            Sentry.captureException(error);
            throw error;
          }
        }

        if (requestId === this.classificationRequestId) {
          const newPagesBySection = { ...this.pagesBySection };

          response.data.forEach(result => {
            if (!this.isPageClassified(result.page)) {
              if (result.form_type === 'filing-instructions') {
                newPagesBySection.filing_instructions = newPagesBySection.filing_instructions.concat(
                  [result.page],
                );
              } else if (result.form_type === 'e-sign-form') {
                newPagesBySection.e_sign_forms = newPagesBySection.e_sign_forms.concat(
                  [result.page],
                );
              } else if (result.form_type === 'payment-voucher') {
                newPagesBySection.payment_vouchers = newPagesBySection.payment_vouchers.concat(
                  [result.page],
                );
              } else if (result.form_type === 'tax-return-page') {
                newPagesBySection.tax_return_pages = newPagesBySection.tax_return_pages.concat(
                  [result.page],
                );
              }
            }
            if (result.form_name) {
              this.guessedPages.push({
                page: result.page,
                form_name: result.form_name,
              });
            }
          });

          const newlyClassifiedPages = response.data.map(result => result.page);

          // If the page was not classified, add it to the tax_return_pages section
          if (
            !newlyClassifiedPages.includes(page) &&
            !this.isPageClassified(page) &&
            !this.taxReturnUseNativePdfReaderForClassificationEnabled
          ) {
            newPagesBySection.tax_return_pages = newPagesBySection.tax_return_pages.concat(
              [page],
            );
          }

          this.setPageRangesFromSections(newPagesBySection);
        }
      } catch (error) {
        console.error('Error classifying page:', error);
        Sentry.captureException(error);
      }
    },

    cancelAutoClassify() {
      this.classificationRequestId = null;
      this.autoClassifying = false;
    },

    getWatermarkStyle(page) {
      const imageEl = document.querySelector(`.thumbnail-page-${page}`);

      if (!imageEl) {
        return;
      }

      // Calculate font size based on image container dimensions
      // 0.1 means 10% of the image width
      const fontSize = imageEl.offsetWidth * 0.1;

      return {
        fontSize: fontSize + 'px',
      };
    },

    changeToUnclassify(page, oldSectionKey, oldSubSectionKey) {
      // Remove page from sub-section (such as Paper-filed returns)
      if (typeof oldSubSectionKey !== 'undefined') {
        const currentPages =
          this.pagesBySection[oldSectionKey][oldSubSectionKey] || [];
        const newPages = currentPages.filter(p => p !== page);

        this.$set(this.pageRangesBySection, oldSectionKey, {
          ...this.pageRangesBySection[oldSectionKey],
          [oldSubSectionKey]: this.convertToRanges(newPages).join(', '),
        });
      } else {
        // Remove page from the old section
        this.$set(
          this.pageRangesBySection,
          oldSectionKey,
          this.convertToRanges(
            this.pagesBySection[oldSectionKey].filter(p => p !== page),
          ).join(', '),
        );
      }
    },

    changePageSection(
      page,
      oldSectionKey,
      newSectionKey,
      oldSubSectionKey,
      newSubSectionKey,
    ) {
      // Add page to sub-section (such as Paper-filed returns)
      if (typeof newSubSectionKey !== 'undefined') {
        const currentPages =
          this.pagesBySection[newSectionKey][newSubSectionKey] || [];

        const newPages = currentPages.concat([page]);

        this.$set(this.pageRangesBySection, newSectionKey, {
          ...this.pageRangesBySection[newSectionKey],
          [newSubSectionKey]: this.convertToRanges(newPages).join(', '),
        });
      }
      // Add page to normal section
      else {
        this.$set(
          this.pageRangesBySection,
          newSectionKey,
          this.convertToRanges(
            this.pagesBySection[newSectionKey].concat([page]),
          ).join(', '),
        );
      }

      if (oldSectionKey) {
        // Remove page from sub-section (such as Paper-filed returns)
        if (typeof oldSubSectionKey !== 'undefined') {
          const currentPages =
            this.pagesBySection[oldSectionKey][oldSubSectionKey] || [];
          const newPages = currentPages.filter(p => p !== page);

          this.$set(this.pageRangesBySection, oldSectionKey, {
            ...this.pageRangesBySection[oldSectionKey],
            [oldSubSectionKey]: this.convertToRanges(newPages).join(', '),
          });
        }
        // Remove page from normal section
        else {
          // Remove page from the old section
          this.$set(
            this.pageRangesBySection,
            oldSectionKey,
            this.convertToRanges(
              this.pagesBySection[oldSectionKey].filter(p => p !== page),
            ).join(', '),
          );
        }
      }
    },

    // async getEstimatedWaitingTime() {
    //   const response = await API.taxReturn.getEstimatedOcrTime({
    //     id: this.taxReturn.id,
    //     companyId: this.currentCompany.id,
    //   });

    //   this.estimatedOcrTime = response.data.estimated_ocr_time;
    // },

    // autoGetEstimatedWaitingTime() {
    //   this.getEstimatedWaitingTimeHandler = setInterval(
    //     this.getEstimatedWaitingTime,
    //     1000,
    //   );
    // },

    openAutoClassifyConfirmationDialog() {
      if (this.editable) {
        this.autoClassifyConfirmationDialogOpened = true;
      }
    },

    addPaperFiledReturnPackage() {
      this.addPaperFiledReturnDialogOpened = true;
    },

    handleAddPaperFiledReturn(returnName) {
      this.addPaperFiledReturnDialogOpened = false;

      if (isEmpty(returnName)) {
        return;
      }

      this.$set(this.pageRangesBySection.paper_filed_returns, returnName, '');
    },

    deletePaperFiledReturnPackage(returnName) {
      this.$set(this.pageRangesBySection, 'paper_filed_returns', {
        ...omit(this.pageRangesBySection.paper_filed_returns, returnName),
      });

      this.selectedSectionKey = null;
      this.selectedSubSectionKey = null;
    },

    classificationForPage(pageIndex) {
      let classification = [];
      if (
        this.convertRangesToArray(
          this.pageRangesBySection.filing_instructions,
        ).includes(pageIndex)
      ) {
        classification.push('filing_instructions');
      }
      if (
        this.convertRangesToArray(
          this.pageRangesBySection.payment_vouchers,
        ).includes(pageIndex)
      ) {
        classification.push('payment_vouchers');
      }
      if (
        this.convertRangesToArray(
          this.pageRangesBySection.e_sign_forms,
        ).includes(pageIndex)
      ) {
        classification.push('e_sign_forms');
      }
      if (
        this.convertRangesToArray(
          this.pageRangesBySection.tax_return_pages,
        ).includes(pageIndex)
      ) {
        classification.push('tax_return_pages');
      }
      if (
        this.convertRangesToArray(
          this.pageRangesBySection.transmittal_letters,
        ).includes(pageIndex)
      ) {
        classification.push('transmittal_letters');
      }

      if (typeof this.pageRangesBySection.paper_filed_returns === 'object') {
        for (const packageName in this.pageRangesBySection
          .paper_filed_returns) {
          const pages = this.convertRangesToArray(
            this.pageRangesBySection.paper_filed_returns[packageName],
          );

          if (pages.includes(pageIndex)) {
            classification.push(`paper_filed_returns`);
          }
        }
      }

      return classification;
    },

    isOverflowing(refName) {
      const el = this.$refs[refName];
      if (!el) return false;
      // If $refs is an array (due to v-for), use the first element
      const domEl = Array.isArray(el) ? el[0] : el;
      return domEl && domEl.scrollWidth > domEl.clientWidth;
    },
  },

  beforeDestroy() {
    this.cleanupResizeObserver();
  },

  async mounted() {
    this.pdfLoading = true;

    // Initialize container width and setup resize observer
    this.updateContainerWidth();
    this.setupResizeObserver();

    try {
      await this.loadThumbnails();

      const sectionsFromTaxReturn = {
        transmittal_letters: Array.isArray(this.taxReturn.transmittal_letters)
          ? this.taxReturn.transmittal_letters.map(form => form.page)
          : [],
        filing_instructions: Array.isArray(this.taxReturn.filing_instructions)
          ? this.taxReturn.filing_instructions.map(form => form.page)
          : [],
        tax_return_pages: Array.isArray(this.taxReturn.tax_return_pages)
          ? this.taxReturn.tax_return_pages.map(form => form.page)
          : [],
        e_sign_forms: Array.isArray(this.taxReturn.e_sign_forms)
          ? this.taxReturn.e_sign_forms.map(form => form.page)
          : [],
        payment_vouchers: Array.isArray(this.taxReturn.payment_vouchers)
          ? this.taxReturn.payment_vouchers
              .filter(form => form.page !== null)
              .map(form => form.page)
          : [],

        paper_filed_returns: Array.isArray(this.taxReturn.paper_filed_returns)
          ? fromPairs(
              this.taxReturn.paper_filed_returns
                .filter(paperFiledReturn => paperFiledReturn.pages)
                .map(paperFiledReturn => {
                  return [
                    paperFiledReturn.custom_return_name,
                    paperFiledReturn.pages.slice(),
                  ];
                }),
            )
          : {},
      };

      this.setPageRangesFromSections(
        this.normalizeClassificationSectionsForThumbnails(
          sectionsFromTaxReturn,
          this.thumbnails.length,
        ),
      );

      // if (!this.isUsingCustomClassificationModel) {
      //   this.autoGetEstimatedWaitingTime();
      // }
    } catch (error) {
      console.error({ error });
    } finally {
      this.pdfLoading = false;
    }
  },

  async created() {
    this.sections = [];
    this.guessedPages = [];

    this.sections.push({
      key: 'transmittal_letters',
      text: 'Transmittal Letters',
      hidden: true,
      description: '',
      color: '#000',
      show_filter: true,
    });

    this.sections.push({
      key: 'tax_return_pages',
      text: 'Tax Return Pages',
      description:
        'Enter the page numbers/ranges for this client to review before e-signing.',
      color: '#136DF4',
      show_filter: true,
    });

    this.sections.push({
      key: 'e_sign_forms',
      text: 'E-Sign Forms',
      description:
        'Enter the page numbers/ranges for this client to e-sign after reviewing the Tax Return Pages.',
      color: '#43a047',
      show_filter: true,
    });

    if (this.taxReturnPaperFiledReturnsEnabled) {
      this.sections.push({
        key: 'paper_filed_returns',
        text: 'Paper-Filed Returns',
        description:
          'Add paper-filed return packages for this client to download, sign and mail them.',
        color: '#f57c00',
        hasSubSections: true,
        show_filter: true,
      });
    }

    if (this.taxReturnWatermark) {
      this.watermark = this.taxReturnWatermark;
    }

    this.sections.push({
      key: 'payment_vouchers',
      text: 'Payment Vouchers',
      description:
        'Enter the page numbers/ranges of all payment vouchers for this client.',
      color: '#6d4c41',
      show_filter: true,
    });

    // If filing instructions parser is enabled, we will show the filing instructions section at the top
    // TODO: In the future, we will always show Filing Instructions at the top despite whether the parser is enabled
    if (this.taxReturnUseFilingInstructionsParser) {
      this.sections.unshift({
        key: 'filing_instructions',
        text: 'Filing Instructions',
        optional: true,
        description: this.showFilingInstructionsToClients
          ? 'Enter the page numbers/ranges for the filing instructions. These pages will be viewable by clients as filing instruction. You can change this setting in firm settings under Preferences'
          : 'Enter the page numbers/ranges for the filing instructions. These pages will NOT be viewable by clients. You can change this setting in firm settings under Preferences',
        color: '#5e35b1',
        show_filter: true,
      });
    } else {
      this.sections.push({
        key: 'filing_instructions',
        text: 'Filing Instructions',
        optional: true,
        description: this.showFilingInstructionsToClients
          ? 'Enter the page numbers/ranges for the filing instructions. These pages will be viewable by clients as filing instruction. You can change this setting in firm settings under Preferences'
          : 'Enter the page numbers/ranges for the filing instructions. These pages will NOT be viewable by clients. You can change this setting in firm settings under Preferences',
        color: '#5e35b1',
        show_filter: true,
      });
    }
  },
};
</script>

<style lang="postcss" scoped>
.scroll-container {
  height: calc(100dvh - 120px);
}

.pdf-preview {
  flex: auto;
  overflow-y: auto;

  height: 100%;
  background-color: var(--gray-100);
}

@media (max-width: 1260px) {
  .pdf-preview {
    min-height: calc(100dvh - 157px);
    max-height: calc(100dvh - 157px);
  }
}

.selection-banner {
  box-shadow: 0px 1px 1px 0px rgba(0, 0, 0, 0.25);
}

.thumbnail-container {
  align-items: center;
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 16px;
  max-width: 100%;
  max-height: 100%;
}

.thumbnail-top {
  width: 100%;
  padding-left: 2px;
}

.thumbnail {
  display: flex;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;

  position: relative;

  &.selectable {
    cursor: pointer;

    &:hover {
      border-color: var(--blue-500);
      box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.25);
    }

    &.selected {
      border-width: 2px;
      border-color: var(--blue-700);
    }
  }
}

.image-container {
  position: relative;
  display: flex;
  flex: auto;
  min-height: 0;
}

.watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-weight: bold;
  color: rgba(0, 0, 0, 0.25);
  pointer-events: none; /* Allows clicks to pass through the watermark */
  white-space: nowrap;
}

.zoom-btn {
  margin-left: 16px;
  color: var(--blue-700);
}

.sidebar {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);
  max-height: calc(100vh - 120px);
  overflow: scroll;
  background-color: #fff;
  border-left: 1px solid var(--gray-300);
}

.right-sidebar {
  min-width: 300px;
  max-width: 300px;
  box-shadow: -1px 1px 3px 0px rgba(0, 0, 0, 0.15);
  padding: 0px 0px 64px;
}

.left-sidebar {
  min-width: 250px;
  max-width: 250px;
  box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.15);
  z-index: 1;
}

@media (max-width: 1260px) {
  .sidebar {
    min-height: calc(100vh - 157px);
    max-height: calc(100vh - 157px);
  }
}

.autoclassify {
  padding: 16px;
  border-bottom: 1px solid var(--gray-300);
}

.sidebar-card {
  box-shadow: none;
}

.sidebar__content {
  display: flex;
  gap: 4px;
  padding: 20px 20px;
  flex-direction: column;
  border-bottom: 1px solid var(--gray-300);
}
.sidebar__content.selected {
  background-color: #e9f4fd;
  box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.25);
}

.sidebar__input {
  box-sizing: border-box;
  height: 40px;
  padding: 0 8px;
  width: 100%;

  background-color: var(--gray-0);
  border: 0;
  border-radius: 4px;
  outline: 1px solid var(--gray-200);

  font-family: var(--font-family-sans);
  line-height: 40px;
}
.sidebar__input.selected:not(:disabled) {
  box-shadow: 0px 1px 4px 1px rgba(0, 0, 0, 0.25);
  outline: 2px solid var(--blue-500);
}

.sidebar__input:disabled {
  background-color: var(--gray-100);

  pointer-events: none;
}

.select-section-list div[role='listitem'] {
  width: 100%;
}

.ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 230px;
}

.auto-classify-btn {
  background-color: #eef5fe;
  border: 1px solid var(--blue-500);
  color: var(--blue-700);
}

.cancel-btn {
  background-color: #eef5fe;
  border: 1px solid var(--blue-500);
  color: var(--blue-700);
}

.index-title {
}

.thumbnail-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  padding: 8px 20px;
  border-radius: 8px;
  flex: auto;
  width: 100%;
}

.icon-btn {
  height: 24px;
  width: 24px;
}
</style>
